import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'
import type {
  ExplicitRating,
  GifReference,
} from '../../../../functions/src/shared/types/gif'
import type {
  ListRetrievalReviewRequest,
  ListRetrievalReviewResponse,
  ApproveRetrievalRecordRequest,
  ApproveRetrievalRecordResponse,
  ReanalyzeRetrievalRecordRequest,
  ReanalyzeRetrievalRecordResponse,
  RetrievalManualEdits,
  RetrievalReviewFilter,
  RetrievalReviewItem,
} from '../../../../functions/src/shared/types/retrieval'

// Re-export the shared types so page components import them from one place.
export type {
  DuplicateCandidate,
  DuplicateReason,
  RetrievalManualEdits,
  RetrievalReviewFilter,
  RetrievalReviewItem,
} from '../../../../functions/src/shared/types/retrieval'

/**
 * Client wrappers for the admin retrieval-review callables (recommendations
 * module). The 1536-float embedding is stripped server-side and never crosses
 * the wire. Re-analysis runs GIF→MP4 → Gemini video analysis → re-embed, which
 * takes ~15–60s, so it gets a generous client timeout.
 */

export interface RetrievalReviewCounts {
  total: number
  approved: number
  unreviewed: number
}

/** Fetch the review queue for a filter, with approved/unreviewed counts. */
export async function listRetrievalReview(
  filter: RetrievalReviewFilter = 'unreviewed'
): Promise<{ items: RetrievalReviewItem[]; counts: RetrievalReviewCounts }> {
  const fn = httpsCallable<
    ListRetrievalReviewRequest,
    ListRetrievalReviewResponse
  >(functions, 'listRetrievalReview', { timeout: 60000 })

  const result = await fn({ filter })
  if (!result.data.success || !result.data.data) {
    throw new Error(
      result.data.error || 'Failed to load retrieval review queue'
    )
  }
  return result.data.data
}

/**
 * Approve a record — which also publishes the gif (searchable + recommendable).
 * Optionally pass the curator-confirmed `explicitRating`/`reference` (defaulted
 * from the record's `proposedMeta` on the review card); both are additive and
 * omitting them preserves the record's existing values. Returns the updated item.
 */
export async function approveRetrievalRecord(
  gifId: string,
  confirmed?: {
    explicitRating?: ExplicitRating
    reference?: GifReference
  }
): Promise<RetrievalReviewItem> {
  const fn = httpsCallable<
    ApproveRetrievalRecordRequest,
    ApproveRetrievalRecordResponse
  >(functions, 'approveRetrievalRecord', { timeout: 30000 })

  const result = await fn({
    gifId,
    ...(confirmed?.explicitRating
      ? { explicitRating: confirmed.explicitRating }
      : {}),
    ...(confirmed?.reference ? { reference: confirmed.reference } : {}),
  })
  if (!result.data.success || !result.data.data) {
    throw new Error(result.data.error || 'Failed to approve record')
  }
  return result.data.data.item
}

/**
 * Re-analyze with curator hints. `hints` is the FULL desired curatorHints text
 * (replace semantics; '' clears). Re-runs analysis with hints injected and
 * rebuilds the embedding. Does NOT auto-approve.
 */
export async function reanalyzeWithHints(
  gifId: string,
  hints: string
): Promise<RetrievalReviewItem> {
  const fn = httpsCallable<
    ReanalyzeRetrievalRecordRequest,
    ReanalyzeRetrievalRecordResponse
  >(functions, 'reanalyzeRetrievalRecord', { timeout: 120000 })

  const result = await fn({ gifId, hints })
  if (!result.data.success || !result.data.data) {
    throw new Error(result.data.error || 'Re-analysis failed')
  }
  return result.data.data.item
}

/**
 * Apply manual field edits. The embedding is rebuilt server-side from the
 * edited text (a text edit without re-embed would desync retrieval). Does NOT
 * auto-approve.
 */
export async function applyManualEdits(
  gifId: string,
  manualEdits: RetrievalManualEdits
): Promise<RetrievalReviewItem> {
  const fn = httpsCallable<
    ReanalyzeRetrievalRecordRequest,
    ReanalyzeRetrievalRecordResponse
  >(functions, 'reanalyzeRetrievalRecord', { timeout: 60000 })

  const result = await fn({ gifId, manualEdits })
  if (!result.data.success || !result.data.data) {
    throw new Error(result.data.error || 'Manual edit failed')
  }
  return result.data.data.item
}
